import { Select, UnstyledButton } from '@mantine/core'
import { IconArrowDown, IconArrowUp, IconSelector } from '@tabler/icons-react'
import type { OrdemDaEntrada } from '@/api/chamados'
import {
  escolhasDeOrdem, ROTULO_DA_COLUNA, SENTIDO_NATURAL, ordemDaEscolha, sentidoPorExtenso,
  valorDaEscolha, type Ordem,
} from './ordem'
import classes from './Entrada.module.css'

interface OrdenacaoProps {
  ordem: Ordem | null
  onOrdenar: (coluna: OrdemDaEntrada) => void
}

function nomeAcessivel(coluna: OrdemDaEntrada, ordem: Ordem | null): string {
  const rotulo = ROTULO_DA_COLUNA[coluna]
  if (ordem?.coluna !== coluna) return `Ordenar por ${rotulo}`
  const proximo = ordem.direcao === SENTIDO_NATURAL[coluna] ? 'inverter' : 'voltar à ordem padrão'
  return `${rotulo}, ordenado: ${sentidoPorExtenso(ordem)}. Clique para ${proximo}.`
}

function ColunaQueOrdena({ coluna, rotulo, area, ordem, onOrdenar }: OrdenacaoProps & {
  coluna: OrdemDaEntrada; rotulo: string; area: string
}) {
  const ativa = ordem?.coluna === coluna
  const Icone = !ativa ? IconSelector : ordem.direcao === 'asc' ? IconArrowUp : IconArrowDown
  return (
    <UnstyledButton
      className={classes.colunaQueOrdena}
      style={{ gridArea: area }}
      data-ativa={ativa}
      aria-label={nomeAcessivel(coluna, ordem)}
      onClick={() => onOrdenar(coluna)}
    >
      <span>{rotulo}</span>
      <Icone size={14} aria-hidden />
    </UnstyledButton>
  )
}

/**
 * Os rótulos das colunas da lista, na mesma grade da linha (spec 2026-10-21 §2.2). #, Solicitante,
 * Responsável, Prioridade e Espera ordenam; Ambiente e Assunto só dizem o que é a coluna.
 * Responsável só nas pastas em que ele varia (spec 2026-10-22 §2). Só no computador: abaixo de
 * 75em a linha vira três e quem ordena é o `SeletorDeOrdem`.
 */
export function CabecalhoDasColunas({ ordem, onOrdenar, comResponsavel }: OrdenacaoProps & {
  comResponsavel: boolean
}) {
  const props = { ordem, onOrdenar }
  return (
    <div className={classes.cabecalho} role="group" aria-label="Ordenar a lista" data-testid="cabecalho-das-colunas">
      <ColunaQueOrdena {...props} coluna="id" rotulo="#" area="id" />
      <ColunaQueOrdena {...props} coluna="solicitante" rotulo="Solicitante" area="solicitante" />
      {comResponsavel && (
        <ColunaQueOrdena {...props} coluna="responsavel" rotulo="Responsável" area="responsavel" />
      )}
      <ColunaQueOrdena {...props} coluna="prioridade" rotulo="Prioridade" area="prioridade" />
      <span className={classes.rotuloDaColuna} style={{ gridArea: 'ambiente' }}>Ambiente</span>
      <span className={classes.rotuloDaColuna} style={{ gridArea: 'conteudo' }}>Assunto</span>
      <ColunaQueOrdena {...props} coluna="espera" rotulo="Espera" area="espera" />
    </div>
  )
}

/** A ordem em tela estreita, onde não há colunas: as mesmas escolhas, por extenso. */
export function SeletorDeOrdem({ ordem, onEscolher, comResponsavel }: {
  ordem: Ordem | null
  onEscolher: (ordem: Ordem | null) => void
  comResponsavel: boolean
}) {
  return (
    <Select
      className={classes.seletorDeOrdem}
      size="xs"
      aria-label="Ordenar"
      placeholder="Ordenar"
      data={escolhasDeOrdem(comResponsavel)}
      value={valorDaEscolha(ordem)}
      onChange={(v) => onEscolher(ordemDaEscolha(v))}
      allowDeselect={false}
      comboboxProps={{ withinPortal: true }}
    />
  )
}
